import React, { useState } from "react";
import { Button, Typography, Textarea } from "@castlery/fortress";
import Drawer from "@/components/ui/drawer";

interface DislikeReasonProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (comment: string) => void;
  onCancel: () => void;
  isVisibleContactSupport?: boolean;
  onContactSupport?: () => void;
  containerRef?: React.RefObject<HTMLDivElement>;
}

const REASONS = [
  "Slow response",
  "Too robotic",
  "Not enough information",
  "Lacked empathy",
  "Irrelevant responses",
  "Too generic",
];

export const DislikeReason: React.FC<DislikeReasonProps> = ({
  open,
  onOpenChange,
  onSubmit,
  onCancel,
  onContactSupport,
  isVisibleContactSupport,
  containerRef,
}) => {
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleReasonClick = (reason: string) => {
    setComment((prev) => {
      const newValue = prev ? `${prev} ${reason}` : reason;
      return newValue.slice(0, 1000);
    });
  };

  const handleSubmit = () => {
    setSubmitting(true);
    onSubmit(comment);
    setSubmitting(false);
    handleClose();
  };

  const handleContactSupport = () => {
    onContactSupport?.();
    handleClose();
  };

  const handleClose = () => {
    onOpenChange(false);
    onCancel();
    // Reset state
    setComment("");
  };

  return (
    <Drawer
      className="z-[1001] sm:z-10"
      open={open}
      onClose={handleClose}
      direction="bottom"
      showCloseButton={true}
      container={containerRef?.current}
    >
      <div className="bg-[#FBF9F4]">
        {/* Header */}
        <div className="py-5 px-6 text-left">
          <div className="flex flex-col justify-between">
            <div className="flex items-center justify-between text-[20px] text-foreground leading-[1.2]">
              <span>Select all that apply (Optional)</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 flex flex-col px-6">
          <div className="flex flex-wrap gap-2 mb-3">
            {REASONS.map((reason) => (
              <div
                key={reason}
                className="cursor-pointer px-[12px] py-[6px] border transition-colors duration-200 bg-transparent border-[#BEBEBE] text-foreground hover:border-[#844025]"
                onClick={() => handleReasonClick(reason)}
              >
                <Typography level="body2" className="text-[#212121]">
                  {reason}
                </Typography>
              </div>
            ))}
          </div>

          <div className="mb-6">
            <Textarea
              placeholder="Tell us more (optional)"
              value={comment}
              onChange={(e) => {
                const value = e.target.value;
                if (value.length <= 1000) {
                  setComment(value);
                } else {
                  setComment(value.slice(0, 1000));
                }
              }}
              endDecorator={
                <Typography
                  level="body2"
                  sx={{
                    ml: "auto",
                  }}
                >
                  {comment.length}/1000
                </Typography>
              }
              sx={{
                minWidth: 300,
                backgroundColor: "transparent",
                "--Textarea-focusedHighlight": "transparent",
                border: "none",
                borderBottom: "1px solid #BEBEBE",
                borderRadius: 0,
                padding: "8px 0",
                "&:focus-within": {
                  borderColor: "#844025",
                  boxShadow: "none",
                },
                "&::placeholder": {
                  color: "#757575",
                  fontStyle: "italic", // Image looks slightly italic or serif? Hard to tell, sticking to standard.
                },
              }}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 pt-0">
          <Button
            className="w-full mb-4 text-[16px] h-[44px] rounded-[8px] font-semibold"
            onClick={handleSubmit}
            disabled={submitting}
            sx={{
              backgroundColor: "#D45D28", // Orange color from image
              "&:hover": {
                backgroundColor: "#B84C1E",
              },
            }}
          >
            SUBMIT
          </Button>

          {isVisibleContactSupport && (
            <div className="flex items-center gap-1 mt-[12px]">
              <Typography level="body2" className="text-[#212121]">
                Need help?
              </Typography>
              <span
                className="text-[#D45D28] underline cursor-pointer text-[14px] hover:text-[#B84C1E]"
                onClick={handleContactSupport}
              >
                Chat with a team member.
              </span>
            </div>
          )}
        </div>
      </div>
    </Drawer>
  );
};
